import brandHex from './brand-colors.json';
import brandHexDark from './brand-colors-dark.json';
import themeRoles from './theme-roles.json';

/**
 * Brand color hex constants — the JSON file `./brand-colors.json` is the
 * single source of truth for the Flashka Design System palette. The
 * Tailwind preset (`tailwind-preset.cjs`) loads the same JSON via
 * CommonJS `require`, so this TS export and the Tailwind palette literally
 * cannot drift.
 *
 * Usage: hex values for places that can't accept a Tailwind class — most
 * notably React Native's `shadowColor` prop, which is a raw color string.
 */
export type BrandPalette =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'green'
  | 'red'
  | 'amber'
  | 'orange'
  | 'lime'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'fuchsia'
  | 'pink'
  | 'rose';
export type BrandShade =
  | '50'
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900'
  | '950';

type BrandHex = Readonly<Record<BrandPalette, Readonly<Record<BrandShade, string>>>>;

export const BRAND_HEX = brandHex as BrandHex;

/**
 * The dark theme's own values (Onyx, `brand-colors-dark.json`) for the tokens
 * that are NOT the mirror — the same overrides `generate-css-vars.cjs` emits,
 * so a raw colour from {@link brandColor} matches the CSS variable.
 */
const DARK_THEME_HEX = brandHexDark as unknown as Partial<
  Record<BrandPalette, Partial<Record<BrandShade, string>>>
>;

/**
 * Dark-mode mirror of a shade: 50↔950, 100↔900, …, 500 unchanged.
 *
 * This is the palette's OWN rule, not a heuristic — `generate-css-vars.cjs`
 * emits each `.dark` variable as `light {name}-{1000 − shade}` (unless the dark
 * theme overrides it — {@link brandColor} applies those) (verified
 * against Figma's per-shade light/dark variable modes). Exported so the
 * handful of places that need a RAW colour (React Native's `shadowColor`,
 * `react-native-svg`'s `stroke`, a lucide icon's `color`) stay theme-correct
 * without hand-tuning a second dark palette.
 */
export const mirrorShade = (shade: BrandShade): BrandShade =>
  String(1000 - Number(shade)) as BrandShade;

/**
 * A brand palette colour for the current theme — the raw-colour equivalent of
 * a `text-{name}-{shade}` / `bg-…` / `stroke-…` class.
 *
 * ```ts
 * const { isDark } = useTheme();
 * <ChartPie color={brandColor('secondary', '600', isDark)} />
 * ```
 */
export const brandColor = (
  palette: BrandPalette,
  shade: BrandShade,
  isDark: boolean,
): string =>
  isDark
    ? (DARK_THEME_HEX[palette]?.[shade] ?? BRAND_HEX[palette][mirrorShade(shade)])
    : BRAND_HEX[palette][shade];

/**
 * The logo's two colours, VIVID in both themes — for a raw fill that has no
 * CSS variable (an SVG gradient on native). Same values as
 * `--logo-front-vivid-rgb` / `--logo-back-vivid-rgb` (generate-css-vars.cjs).
 */
export const LOGO_VIVID_HEX = { front: '#fcd34d', back: '#a78bfa' } as const;

/**
 * Semantic colour roles (`./theme-roles.json`) — named uses of the palette,
 * shared by web and mobile. Same rule as `generate-css-vars.cjs`, which
 * writes the `--{role}-rgb` variables behind the `bg-surface-raised` /
 * `text-fg-muted` / … classes, so `roleColor` and the class cannot disagree:
 * dark is the token's dark value (Onyx, else the mirror — {@link brandColor})
 * unless the role is `stable` or names an explicit `dark` token, itself read in
 * the dark palette.
 */
export type ThemeRole = keyof typeof themeRoles.roles;

interface RoleDef {
  light: string;
  dark?: string;
  stable?: boolean;
}

const tokenHex = (token: string): string => {
  const [palette, shade] = token.split('.') as [BrandPalette, BrandShade];
  return BRAND_HEX[palette][shade].toLowerCase();
};

/**
 * A role's colour for the current theme — the raw-colour equivalent of its
 * class, for props that cannot take one (a lucide icon's `color`, an SVG
 * `stroke`, RN `shadowColor`, CSS injected into a WebView).
 *
 * ```ts
 * const { isDark } = useTheme();
 * <ChevronRight color={roleColor('fg-muted', isDark)} />
 * ```
 */
const resolveRole = (role: ThemeRole, isDark: boolean): string => {
  const def = themeRoles.roles[role] as RoleDef;
  if (!isDark || def.stable) return tokenHex(def.light);
  const [palette, shade] = (def.dark ?? def.light).split('.') as [
    BrandPalette,
    BrandShade,
  ];
  return brandColor(palette, shade, true).toLowerCase();
};

/** Resolved once at load: `roleColor` runs in render paths (rows, scroll fades). */
const ROLE_HEX = Object.fromEntries(
  (Object.keys(themeRoles.roles) as ThemeRole[]).map((role) => [
    role,
    [resolveRole(role, false), resolveRole(role, true)] as const,
  ]),
) as Record<ThemeRole, readonly [light: string, dark: string]>;

export const roleColor = (role: ThemeRole, isDark: boolean): string =>
  ROLE_HEX[role][isDark ? 1 : 0];

/** The page colour — the existing `background` token (`bg-background`). */
export const pageColor = (isDark: boolean): string =>
  themeRoles.page[isDark ? 'dark' : 'light'].toLowerCase();

/** The dotted texture's dot colour (`theme-roles.json` `dot`), drawn on {@link pageColor}. */
export const dotColor = (isDark: boolean): string =>
  themeRoles.dot[isDark ? 'dark' : 'light'];

/** Every role name, in table order. */
export const THEME_ROLES = Object.keys(themeRoles.roles) as ThemeRole[];
